import { useEffect } from "react";
import type { Episode } from "@supermanus/document-model";
import { formatEpisodeDisplayTitle } from "@supermanus/document-model";
import type { NoteColumnContentReport } from "@supermanus/document-model";

export type RemoveNoteColumnScope = "episode" | "production";

export interface RemoveNoteColumnDialogProps {
  columnTitle: string;
  report: NoteColumnContentReport;
  showEpisodeNumbers: boolean;
  onCancel: () => void;
  onConfirm: (scope: RemoveNoteColumnScope) => void;
}

function formatEpisodeList(
  episodes: Episode[],
  showEpisodeNumbers: boolean,
): string {
  return episodes
    .map((ep) => formatEpisodeDisplayTitle(ep, showEpisodeNumbers))
    .join(", ");
}

function contentSummary(
  report: NoteColumnContentReport,
  showEpisodeNumbers: boolean,
): string {
  const current = report.currentEpisodeEmpty
    ? "Kolonnen er tom i denne episode."
    : "Kolonnen indeholder noter i denne episode.";

  if (report.allEpisodesEmpty) {
    return `${current} Den er også tom i alle andre episoder.`;
  }

  if (report.otherEpisodesWithContent.length === 0) {
    return `${current} Den er tom i alle andre episoder.`;
  }

  const names = formatEpisodeList(
    report.otherEpisodesWithContent,
    showEpisodeNumbers,
  );
  return `${current} Den indeholder noter i: ${names}.`;
}

export function RemoveNoteColumnDialog({
  columnTitle,
  report,
  showEpisodeNumbers,
  onCancel,
  onConfirm,
}: RemoveNoteColumnDialogProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const label = columnTitle.trim() || "Noter";

  return (
    <div
      className="sm-dialog-backdrop"
      role="presentation"
      onClick={onCancel}
    >
      <div
        className="sm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sm-remove-note-col-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="sm-remove-note-col-title">Fjern note-kolonne</h2>
        <p>
          Vil du fjerne kolonnen «{label}» kun i denne episode, eller i alle
          episoder?
        </p>
        <p className="sm-dialog-hint">{contentSummary(report, showEpisodeNumbers)}</p>
        <div className="sm-dialog-actions sm-dialog-actions-stack">
          <button
            type="button"
            className="sm-dialog-primary"
            onClick={() => onConfirm("episode")}
          >
            Kun denne episode
          </button>
          <button
            type="button"
            className="sm-dialog-danger"
            onClick={() => onConfirm("production")}
          >
            Alle episoder
          </button>
          <button type="button" onClick={onCancel}>
            Annuller
          </button>
        </div>
      </div>
    </div>
  );
}
